import React, { useState } from "react";
import { toast } from "sonner";
import MarketingLayout from "@/components/MarketingLayout";
import { api } from "@/lib/api";
import { CheckCircle2, Calendar, PhoneCall } from "lucide-react";

const PROVINCES = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon", "Northwest Territories", "Nunavut"];

export default function Demo() {
  const [form, setForm] = useState({
    name: "", dealership: "", email: "", phone: "", city: "", province: "Ontario",
    num_locations: 1, best_time: "", current_system: "", approximate_inventory: "",
    interested_plan: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: k === "num_locations" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      // Re-use existing early-access endpoint — same lead capture pipe.
      const notes = `[Demo request] Phone: ${form.phone || "—"}. Best time: ${form.best_time || "—"}. Approx inventory: ${form.approximate_inventory || "—"}. Current system: ${form.current_system || "—"}.`;
      await api.post("/early-access", {
        name: form.name, dealership: form.dealership, email: form.email,
        city: form.city, province: form.province, num_locations: form.num_locations,
        current_system: notes, approximate_inventory: form.approximate_inventory,
        interested_plan: "Demo request",
      });
      setDone(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <MarketingLayout>
      <div className="tm-container py-16 max-w-2xl">
        <div className="tm-demo-tag mb-4 inline-flex items-center gap-1.5"><Calendar size={12} /> Book a 15-min demo</div>
        <h1 className="text-3xl sm:text-4xl font-bold" data-testid="demo-heading">See TreadMargin on your own data.</h1>
        <p className="mt-3 text-[#3B424B] max-w-lg">
          Bring your POS export — I'll walk you through your own Monday-morning buying list live in 15 minutes.
          If it saves you time or catches something you didn't already know, we talk about the founding-dealer plan.
          Otherwise, no pressure — I get feedback, you get a look at the tool.
        </p>

        {done ? (
          <div className="tm-card p-8 mt-8 flex items-start gap-4" data-testid="demo-success">
            <CheckCircle2 className="text-[#14624A] shrink-0" size={28} />
            <div>
              <div className="font-semibold text-lg">Thanks — I'll reach out within 24 hours.</div>
              <p className="mt-2 text-sm text-[#3B424B]">
                We'll pick a 15-min slot that works for you and I'll come with your first buying list already prepped. Talk soon.
              </p>
              <p className="mt-3 text-xs text-[#3B424B]">— Shish, TreadMargin</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="tm-card p-6 sm:p-8 mt-8 space-y-4" data-testid="demo-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="tm-label">Your name *</label><input required className="tm-input" value={form.name} onChange={on("name")} data-testid="demo-name" /></div>
              <div><label className="tm-label">Dealership *</label><input required className="tm-input" value={form.dealership} onChange={on("dealership")} data-testid="demo-dealership" /></div>
              <div><label className="tm-label">Email *</label><input required type="email" className="tm-input" value={form.email} onChange={on("email")} data-testid="demo-email" /></div>
              <div><label className="tm-label">Phone (optional)</label><input type="tel" className="tm-input" placeholder="+14165551234" value={form.phone} onChange={on("phone")} data-testid="demo-phone" /></div>
              <div><label className="tm-label">City *</label><input required className="tm-input" value={form.city} onChange={on("city")} data-testid="demo-city" /></div>
              <div>
                <label className="tm-label">Province *</label>
                <select className="tm-input" value={form.province} onChange={on("province")} data-testid="demo-province">
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label className="tm-label">Locations</label><input type="number" min={1} className="tm-input" value={form.num_locations} onChange={on("num_locations")} data-testid="demo-locations" /></div>
              <div><label className="tm-label">Best time to call</label>
                <select className="tm-input" value={form.best_time} onChange={on("best_time")} data-testid="demo-best-time">
                  <option value="">Anytime</option>
                  <option>Weekday mornings (8-11 ET)</option>
                  <option>Weekday afternoons (1-5 ET)</option>
                  <option>Weekday evenings (5-8 ET)</option>
                  <option>Saturday morning</option>
                </select>
              </div>
              <div className="sm:col-span-2"><label className="tm-label">Current POS / shop management (optional)</label><input className="tm-input" placeholder="e.g. TCS Tire Power, Blackpurl, Bay-Master, Excel" value={form.current_system} onChange={on("current_system")} data-testid="demo-system" /></div>
              <div className="sm:col-span-2"><label className="tm-label">Approximate tire inventory (optional)</label><input className="tm-input" placeholder="e.g. 800 tires" value={form.approximate_inventory} onChange={on("approximate_inventory")} data-testid="demo-inventory" /></div>
            </div>
            <button type="submit" disabled={busy} className="tm-btn-gold w-full sm:w-auto inline-flex items-center gap-2" data-testid="demo-submit">
              <PhoneCall size={14} /> {busy ? "Submitting…" : "Book my demo"}
            </button>
            <p className="text-xs text-[#3B424B]">
              We'll never share your details. Independent tire dealers only.
            </p>
          </form>
        )}
      </div>
    </MarketingLayout>
  );
}
